import { PasswordValidationSchema } from '@airship-platform/common/validation';
import {
  EmptyState,
  FlexBox,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { useAccountManagement } from './contexts/AccountManagementContext';

const resetPasswordSchema = z
  .object({
    password: PasswordValidationSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: 'Passwords must match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormInputs = z.infer<typeof resetPasswordSchema>;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const { resetPassword, isLoading, error } = useAccountManagement();

  const { control, handleSubmit } = useForm<ResetPasswordFormInputs>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(resetPasswordSchema),
  });

  if (!token) {
    return (
      <EmptyState
        title="Invalid or missing token"
        subtitle="Please check your reset password link and try again."
      />
    );
  }

  const handleResetPassword = async (data: ResetPasswordFormInputs) => {
    await resetPassword(token, data.password);

    if (!error) {
      navigate('/signin');
    }
  };

  return (
    <FlexBox flexDirection={'column'} gap={3} flex={1} maxWidth={'25rem'}>
      <FlexBox display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant="h2" color={'secondary'}>
          Reset Password
        </Typography>
        <Typography variant="body1">
          Please enter and confirm your new password to reset your account.
        </Typography>
      </FlexBox>
      <Box
        component={'form'}
        onSubmit={handleSubmit(handleResetPassword)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Password"
              type="password"
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Confirm Password"
              type="password"
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <LoadingButton
          loading={isLoading}
          disabled={isLoading}
          type="submit"
          fullWidth
        >
          Reset Password
        </LoadingButton>
      </Box>
    </FlexBox>
  );
};

export default ResetPassword;
